
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UUID, ISODateTimeString } from '@dopomogai/types';
import { getApiClient } from '@dopomogai/api-client';
import { AgentKitError } from '../types';

type Status = 'idle' | 'fetching_token' | 'ready' | 'error';

export interface PreviewTokenResponse {
  token: string;
  ttl?: number;
  expires_at?: number | ISODateTimeString;
  version_id?: UUID;
  effective_agent_id?: UUID;
}

export type UseWidgetPreviewOptions = {
  // Live config overrides to preview without saving a version
  overrides?: Record<string, any>;
  // Preview with a different agent (BE validates access)
  previewAgentId?: UUID | null;
  // Auto-create token and set script on mount/version change
  autoOnMount?: boolean;
  // Debounce (ms) for override changes
  debounceMs?: number;
  // Error callback
  onError?: (err: AgentKitError) => void;
};

const DEFAULT_DEBOUNCE = 500;
const TOKEN_REFRESH_THRESHOLD_MS = 30_000; // refresh if less than 30s left

function msUntil(expiresAt: number | ISODateTimeString | null | undefined): number {
  if (!expiresAt) return 0;
  const t = typeof expiresAt === 'number' ? expiresAt * 1000 : new Date(expiresAt).getTime();
  const now = Date.now();
  return Math.max(0, t - now);
}

function base64UrlEncodeUtf8(jsonStr: string): string {
  try {
    if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
      const utf8 = new TextEncoder().encode(jsonStr);
      let binary = '';
      utf8.forEach(b => (binary += String.fromCharCode(b)));
      const b64 = window.btoa(binary);
      return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    }
  } catch {}
  try {
    // Node fallback (SSR)
    // @ts-ignore
    const b64 = Buffer.from(jsonStr, 'utf-8').toString('base64');
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch {
    return '';
  }
}

function buildPreviewScriptTag(apiBase: string, token: string, overrides?: Record<string, any> | null, previewAgentId?: UUID | null) {
  const payload: Record<string, any> = { ...(overrides || {}) };
  if (previewAgentId) payload.aid = previewAgentId;
  const encoded = base64UrlEncodeUtf8(JSON.stringify(payload));
  const q = encoded ? `?overrides=${encoded}` : '';
  const src = `${apiBase.replace(/\/$/, '')}/api/v1/widgets/previews/${encodeURIComponent(token)}/serve.js${q}`;
  return `<script src="${src}"></script>`;
}

export const useWidgetPreview = (
  versionId: UUID | null,
  {
    overrides,
    previewAgentId,
    autoOnMount = true,
    debounceMs = DEFAULT_DEBOUNCE,
    onError,
  }: UseWidgetPreviewOptions = {}
) => {
  const api = getApiClient();
  const axios = api._axios();
  const apiBase = api._rawApiBaseUrl();

  const [tokenData, setTokenData] = useState<PreviewTokenResponse | null>(null);
  const [script, setScript] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<AgentKitError | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);

  const debounceTimerRef = useRef<number | null>(null);
  const clearDebounce = () => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  const setErr = useCallback((e: AgentKitError) => {
    setError(e);
    setStatus('error');
    onError?.(e);
  }, [onError]);

  const issueToken = useCallback(async (): Promise<PreviewTokenResponse> => {
    if (!versionId) {
      const err = new AgentKitError('Version ID is required for preview.');
      setErr(err);
      throw err;
    }
    setStatus('fetching_token');
    try {
      // POST /api/v1/widgets/versions/{version_id}/preview-token
      const body: any = {};
      if (previewAgentId) body.agent_id = previewAgentId;

      const res = await axios.post(`/widgets/versions/${versionId}/previews`, body);
      const data = res?.data as PreviewTokenResponse;
      if (!data || !data.token) throw new Error('Invalid preview-token response.');
      setTokenData(data);
      setError(null);
      setStatus('idle');
      return data;
    } catch (e: any) {
      const err = e instanceof AgentKitError ? e : new AgentKitError(e?.message || 'Failed to create preview token.', e);
      setErr(err);
      throw err;
    }
  }, [axios, versionId, previewAgentId, setErr]);

  const computeScript = useCallback((tok: string) => {
    try {
      const s = buildPreviewScriptTag(apiBase, tok, overrides, previewAgentId || null);
      setScript(s);
      setLastUpdatedAt(Date.now());
      setError(null);
      setStatus('ready');
      return s;
    } catch (e: any) {
      const err = e instanceof AgentKitError ? e : new AgentKitError('Failed to build preview script.', e);
      setErr(err);
      return null;
    }
  }, [apiBase, overrides, previewAgentId, setErr]);

  const refresh = useCallback(async () => {
    const tok = await issueToken();
    computeScript(tok.token);
  }, [issueToken, computeScript]);

  // auto token on mount / version change / agent override change
  useEffect(() => {
    if (!versionId) {
      setTokenData(null);
      setScript(null);
      setStatus('idle');
      return;
    }
    if (autoOnMount) void refresh();
  }, [versionId, previewAgentId, autoOnMount, refresh]);

  // On overrides change, reuse token and only rebuild the script (debounced).
  const overridesStableKey = useMemo(() => {
    try {
      return JSON.stringify({ ...(overrides || {}), aid: previewAgentId || null });
    } catch {
      return String(overrides || '') + (previewAgentId || '');
    }
  }, [overrides, previewAgentId]);

  useEffect(() => {
    if (!versionId) return;
    if (!tokenData?.token) return;
    // If token near expiry, re-issue
    const msLeft = msUntil(tokenData.expires_at);
    if (msLeft < TOKEN_REFRESH_THRESHOLD_MS) {
      void refresh();
      return;
    }
    clearDebounce();
    debounceTimerRef.current = window.setTimeout(() => {
      computeScript(tokenData.token);
    }, debounceMs) as unknown as number;
    return () => clearDebounce();
  }, [overridesStableKey, tokenData, debounceMs, versionId, refresh, computeScript]);

  return {
    tokenData,
    script,
    status,
    isLoading: status === 'fetching_token',
    error,
    lastUpdatedAt,
    refresh,   // manual token reload
    apiBase,   // export base URL for proxy render etc.
  };
};